import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PriceDto, ProductDto } from '@nusign/picflow-backend-types';
import { from, map, of, switchMap, take } from 'rxjs';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { doFireworks } from '../../../lib/fireworks';
import { isFreeSubscription } from '../../../lib/subscription-helpers';
import { CardService } from '../../../services/card.service';
import { LoggedInAnalyticsService } from '../../../services/loggedin-analytics.service';
import { PricingService } from '../../../services/pricing.service';
import { ProductService } from '../../../services/product.service';
import { PromotionService } from '../../../services/promotion.service';
import { StateService } from '../../../services/state.service';
import { StripeService } from '../../../services/stripe.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { TenantService } from '../../../services/tenant.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SimpleSwitcherComponent } from '../../../shared/components/simple-switcher/simple-switcher.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { MonthYearDiffPipe } from '../../../shared/pipes/month-year-diff.pipe';
import { CheckoutMode } from '../../../types/checkout-mode';
import { Product, RecurringInterval } from '../../../types/product.type';
import { Promotion } from '../../../types/promotion.type';
import { CardFormComponent } from '../card-form/card-form.component';
import { CheckoutCouponComponent } from '../checkout-coupon/checkout-coupon.component';
import { CheckoutExistingCardComponent } from '../checkout-existing-card/checkout-existing-card.component';
import { CheckoutPlanChangeBoxComponent } from '../checkout-plan-change-box/checkout-plan-change-box.component';
import { CheckoutPricingDetailsComponent } from '../checkout-pricing-details/checkout-pricing-details.component';

@Component({
  selector: 'app-checkout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
  imports: [
    CardFormComponent,
    CheckoutCouponComponent,
    CheckoutPricingDetailsComponent,
    SimpleSwitcherComponent,
    ButtonDirective,
    SvgComponent,
    BadgeComponent,
    CheckoutExistingCardComponent,
    CheckoutPlanChangeBoxComponent,
    LoadingIndicatorComponent,
    CapitalizePipe,
    TransitionPreventorDirective,
    MonthYearDiffPipe,
  ],
})
export class CheckoutComponent {
  private state = inject(StateService);
  private pricingService = inject(PricingService);
  private promotionService = inject(PromotionService);
  private stripeService = inject(StripeService);
  private cardService = inject(CardService);
  private toasts = inject(ToastService);
  private subscriptionService = inject(SubscriptionService);
  private tenantService = inject(TenantService);
  private loggedInAnalytics = inject(LoggedInAnalyticsService);
  private productService = inject(ProductService);
  private router = inject(Router);

  @ViewChild('cardForm') cardForm: CardFormComponent;
  @ViewChild('pricingDetails') pricingDetails: CheckoutPricingDetailsComponent;
  product = input.required<Product>();
  initialBillingFrequency = input.required<RecurringInterval>();
  mainPrice = toSignal(this.subscriptionService.getMainPrice());
  billingFrequency = linkedSignal<RecurringInterval>(() => {
    const initialBillingFrequency = this.initialBillingFrequency();
    const currentBillingFrequency = this.mainPrice()?.recurring.interval as RecurringInterval;
    return initialBillingFrequency || currentBillingFrequency;
  });
  price = computed(
    () =>
      this.pricingService.getDefaultPrice(this.billingFrequency(), this.product().prices) as PriceDto & {
        product: ProductDto;
      }
  );
  hideChangeBox = input<boolean>();
  newPromotion = input<Promotion>();
  currentDiscount = toSignal(this.subscriptionService.getCurrentDiscount());
  promotion = computed(() => {
    const currentDiscount = this.currentDiscount();
    const promotion = this.newPromotion() || currentDiscount?.promo;
    const price = this.price();
    if (!promotion || !price) return;
    if (this.promotionService.appliesToPrice(promotion, price)) return promotion;
  });
  endTrialEarly = input.required<boolean>();

  validPromotion = computed(() => {
    const promotion = this.promotion();
    const price = this.price();
    if (!promotion || !price) return false;
    return this.promotionService.appliesToPrice(promotion, price);
  });
  cardData = toSignal(this.cardService.getCardData());
  existingCardMode = signal(!!this.cardData()?.card);

  showPlanChange = computed(() => {
    const currentProduct = this.mainPrice()?.product;
    const newProduct = this.product();
    const hideChangeBox = this.hideChangeBox();
    const subscription = this.subscription();
    if (!currentProduct || !newProduct) return false;
    if (hideChangeBox) return false;
    if (isFreeSubscription(subscription)) return false;
    return currentProduct.id !== newProduct.id;
  });

  products = toSignal(from(this.productService.getAllProducts()));
  subscription = toSignal(this.subscriptionService.getSubscription());
  trialDays = this.pricingService.getTrialLengthDays();
  canStartTrial = computed(() => this.pricingService.canStartTrial(this.subscription()));
  activeTenant = toSignal(this.state.activeTenant$);

  legacyPrice = computed(() => {
    const subscription = this.subscription();
    const products = this.products();
    const mainPrice = this.mainPrice();
    if (!products) return;
    if (isFreeSubscription(subscription)) return false;
    const product = products.find(p => p.id === mainPrice.product.id);
    if (!product) return false;
    return !product.prices.some(price => price.id === mainPrice.id) && !product.isFree;
  });
  mode = computed<CheckoutMode>(() => {
    const currentProduct = this.mainPrice()?.product;
    const newProduct = this.product();
    const billingFrequency = this.billingFrequency();
    const isOnLegacyPrice = this.legacyPrice();
    const subscription = this.subscription();
    const mainPrice = this.mainPrice();
    const endTrialEarly = this.endTrialEarly();
    if (isOnLegacyPrice === undefined) return;
    if (!billingFrequency) return;

    if (this.canStartTrial()) return 'start-trial';
    if (currentProduct.id === newProduct.id) {
      const isTrialing = subscription?.status === 'trialing';

      if (isTrialing && endTrialEarly) {
        return 'upgrade-early';
      }

      if (isOnLegacyPrice) {
        return 'upgrade';
      }

      if (!isTrialing && mainPrice.recurring.interval !== billingFrequency) {
        return 'change-frequency';
      }
      return 'upgrade';
    }
    return this.pricingService.isUpgrade(mainPrice, this.product()) ? 'upgrade' : 'downgrade';
  });

  submitting = signal(false);

  private isValidCard() {
    if (this.existingCardMode()) return true;
    this.cardForm.formSubmitted.set(true);
    return !this.cardForm.anyIssues();
  }

  private getOrCreateCard() {
    if (this.existingCardMode()) return this.cardService.getCardData().pipe(take(1));
    return this.stripeService.createCard(this.cardForm).pipe(
      switchMap(resp => {
        if (resp.error) {
          console.error(resp.error);
          return of(null);
        }
        return this.cardService.forceCardRefresh().pipe(take(1));
      })
    );
  }

  private createSubscription() {
    return this.subscriptionService.createSubscription(this.price(), this.promotion(), this.canStartTrial()).pipe(
      switchMap(resp => {
        if (!resp) return of(null);

        return from(this.tenantService.pollForTenantChange(tenant => !!tenant.id_stripe_subscription)).pipe(
          map(resp => {
            if (!resp) {
              this.toasts.add('Something went wrong, please try again', 'error');
              return false;
            }
            this.tenantService.updateActiveTenantLocal({ id_stripe_subscription: resp.id_stripe_subscription });
            return true;
          })
        );
      })
    );
  }

  private updateSubscription() {
    const prices: { id: string; quantity?: number }[] = [{ id: this.price().id }];
    const startTrial = this.canStartTrial();
    const trialLength = this.pricingService.getTrialLengthDays();
    const endTrialEarly = this.mode() === 'upgrade-early';
    const promo = this.promotion();
    return this.subscriptionService
      .updateSubscription({
        prices,
        trial_days: startTrial ? trialLength : endTrialEarly ? 0 : undefined,
        promotion_code: promo?.code,
      })
      .pipe(map(resp => !!resp));
  }

  confirm() {
    if (this.submitting()) return;
    this.cardForm?.formSubmitted.set(true);
    if (!this.isValidCard()) return;

    this.submitting.set(true);
    this.getOrCreateCard().subscribe(cardData => {
      if (!cardData?.card) return this.submitting.set(false);
      const mode = this.mode();
      const product = this.product();
      const price = this.price();
      const hasSubscription = !!this.activeTenant().id_stripe_subscription;
      const canStartTrial = this.canStartTrial();
      const observable = hasSubscription ? this.updateSubscription() : this.createSubscription();

      observable.pipe(take(1)).subscribe(resp => {
        this.submitting.set(false);
        if (!resp) return;
        this.sendEvents(hasSubscription, canStartTrial);

        if (canStartTrial) {
          this.router.navigate(['/a', 'home']);
          this.toasts.add('Your trial has started!', 'success');
          doFireworks();
        } else {
          this.router.navigate(['/a', 'home'], {
            queryParams: {
              confirmation: 'true',
              mode,
              product: product.name.toLowerCase(),
              bf: price.recurring.interval,
            },
          });
        }
      });
    });
  }

  private sendEvents(hadSubscription: boolean, canStartTrial: boolean) {
    const total = this.pricingDetails.totalAmount() / 100;
    if (hadSubscription) {
      this.loggedInAnalytics.changePlanEvent(this.product(), this.price(), this.promotion(), total);
    } else {
      if (canStartTrial) {
        this.loggedInAnalytics.startTrialEvent(this.product(), this.price(), this.promotion(), total);
      } else {
        // backend sends event
      }
    }
  }
}
